/**
 * The one reading window Território and Sentimento share: a selectable
 * duration, not a fixed granularity, so both screens mount this component
 * rather than each keeping its own.
 *
 * `useWindowSelection` keeps the choice in `?window=` and remembers it per
 * screen, so a reload or a return reads the same window back.
 */

import { useState, type ReactNode } from 'react';

import { useViewSearch, type ViewSpec, type ViewValues } from '../../../chrome/viewSearch';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';
import type { Window } from '../api/queries';
import { ds } from './messages';

export const WINDOW_HOURS = { '6h': 6, '1d': 24, '3d': 72, '7d': 168 } as const;
export type WindowKey = keyof typeof WINDOW_HOURS;
export const WINDOW_KEYS = Object.keys(WINDOW_HOURS) as WindowKey[];
export const DEFAULT_WINDOW_KEY: WindowKey = '7d';

const HOUR_MS = 3_600_000;

/** The `[from, to]` range a window key names, ending at `now`. */
export function windowFor(key: WindowKey, now: Date): Window {
  return {
    from: new Date(now.getTime() - WINDOW_HOURS[key] * HOUR_MS).toISOString(),
    to: now.toISOString(),
  };
}

/** The bucket a bucketed series read asks for at this window: hour under a
 *  day, day otherwise. */
export function bucketFor(key: WindowKey): 'hour' | 'day' {
  return WINDOW_HOURS[key] < 24 ? 'hour' : 'day';
}

/** The window field of a view's search spec, for `useViewSearch`. */
export const WINDOW_FIELD = { default: DEFAULT_WINDOW_KEY, values: WINDOW_KEYS } as const;

/** Reads `?window=` through `useViewSearch` (so it is remembered per `view`),
 *  beside any other fields the view keeps. The range is fixed once per mount:
 *  a range recomputed from the clock on every render would be a new query key
 *  each render, and the queries built from it would never settle. */
export function useWindowSelection<S extends ViewSpec>(
  view: string,
  extra?: S,
): {
  key: WindowKey;
  window: Window;
  onChange: (key: WindowKey) => void;
  values: ViewValues<S>;
  set: (patch: Partial<ViewValues<S>>) => void;
} {
  const [values, set] = useViewSearch(view, { ...(extra ?? ({} as S)), window: WINDOW_FIELD });
  const [now] = useState(() => new Date());
  const key = values.window as WindowKey;

  return {
    key,
    window: windowFor(key, now),
    onChange: (next) => set({ window: next } as Partial<ViewValues<S>>),
    values: values as ViewValues<S>,
    set: set as (patch: Partial<ViewValues<S>>) => void,
  };
}

export function WindowSelect(props: {
  value: WindowKey;
  onChange: (key: WindowKey) => void;
}): ReactNode {
  return (
    <ToggleGroup
      type="single"
      spacing={1}
      aria-label={ds('window.label')}
      value={props.value}
      onValueChange={(next) => {
        // Radix reports an empty string when the selected item is pressed
        // again; a segmented selector always has one option chosen.
        if (next !== '') props.onChange(next as WindowKey);
      }}
      className="bg-secondary rounded-pill p-1"
    >
      {WINDOW_KEYS.map((key) => (
        <ToggleGroupItem key={key} value={key} className={SEGMENTED_ITEM_CLASSNAME}>
          {key}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
